import {
  BG_STYLES,
  BORDER_STYLES,
  // CaretDownIcon,
  COLORS,
  COMMON_STYLES,
  formatCurrency,
  LazyImage,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";
import React from "react";
import {
  View,
  Text,
  StyleSheet,
  TextStyle,
  ViewStyle,
  StyleProp,
  TouchableOpacity,
} from "react-native";

// import { rnNavigation } from 'app/utils/appConfig'
import { OrderLine } from "../../models/orders";
import ProductIcon from "../../icons/ProductIcon";
import { ORDER_STATUS } from "../../constants/order";

interface Props {
  orderId: string;
  date: string;
  price: string;
  name: string;
  orderLines: OrderLine[];
  status: ORDER_STATUS;
  containerStyle?: StyleProp<ViewStyle>;
  onClick: () => void;
}

const ImageCountView = ({ count }: { count: number }) => {
  return (
    <View
      style={[
        styles.img80X90,
        COMMON_STYLES.b1,
        BORDER_STYLES.borderNeutral10,
        COMMON_STYLES["br1/2"],
        BG_STYLES.bgNeutral10,
        COMMON_STYLES.alignItemsCenter,
        COMMON_STYLES.justifyCenter,
      ]}
    >
      <Text
        style={[
          TYPOGRAPHY.HEADING3,
          VARIANTS_STYLES.MEDIUM,
          TEXT_STYLES.textNeutral90,
        ]}
      >
        +{count}
      </Text>
    </View>
  );
};

const OrderListingCard = ({
  orderId,
  date,
  price,
  name,
  orderLines,
  status,
  containerStyle,
  onClick,
}: Props) => {
  const getStatusColour = (): [TextStyle, ViewStyle] => {
    switch (status) {
      case ORDER_STATUS.PaymentFailed:
      case ORDER_STATUS.DeHaatCancelled:
      case ORDER_STATUS.UserCancelled:
      case ORDER_STATUS.VendorCancelled:
      case ORDER_STATUS.OrderFailed:
      case ORDER_STATUS.ReturnToOrigin:
        return [TEXT_STYLES.textError100, BG_STYLES.bgError10];
      case ORDER_STATUS.Delivered:
        return [TEXT_STYLES.textPrimary100, BG_STYLES.bgPrimary10];
      default:
        return [{ color: "#8C6500" }, { backgroundColor: "#FFF3C2" }];
    }
  };

  const getUpdatedStatus = () => {
    switch (status) {
      case ORDER_STATUS.PaymentFailed:
      case ORDER_STATUS.OrderFailed:
        return "Order Failed";
      case ORDER_STATUS.DeHaatCancelled:
      case ORDER_STATUS.UserCancelled:
      case ORDER_STATUS.VendorCancelled:
      case ORDER_STATUS.ReturnToOrigin:
        return "Order Cancelled";
      case ORDER_STATUS.OutForDelivery:
        return "Out for delivery";
      case ORDER_STATUS.Delivered:
        return "Delivered";
      case ORDER_STATUS.PreOrder:
        return "PRE ORDER";
      case ORDER_STATUS.VendorConfirmed:
      case ORDER_STATUS.ReadyForDelivery:
        return "Vendor Confirmed";
      case ORDER_STATUS.Shipped:
        return "Shipped";

      default:
        return "Order Pending";
    }
  };

  const [textColor, bgColor] = getStatusColour();

  return (
    <TouchableOpacity
      onPress={onClick}
      style={[
        COMMON_STYLES.b1,
        BORDER_STYLES.borderBlue20,
        BG_STYLES.bgWhite,
        COMMON_STYLES.br1,
        COMMON_STYLES.ofHidden,
        styles.container,
        containerStyle,
      ]}
    >
      <View
        style={[
          COMMON_STYLES.ph1,
          COMMON_STYLES["pv1/2"],
          COMMON_STYLES.relative,
          styles.header,
        ]}
      >
        <Text
          style={[
            TYPOGRAPHY.TEXT1,
            VARIANTS_STYLES.MEDIUM,
            TEXT_STYLES.textNeutral90,
          ]}
        >
          Order ID: {orderId}
        </Text>
        <Text
          style={[
            TYPOGRAPHY.TEXT2,
            VARIANTS_STYLES.MEDIUM,
            TEXT_STYLES.textNeutral80,
          ]}
          numberOfLines={1}
        >
          {date} | {formatCurrency(price)} | {name}
        </Text>
        <View
          style={[
            COMMON_STYLES["pv1/4"],
            COMMON_STYLES["ph1/2"],
            COMMON_STYLES.absolute,
            COMMON_STYLES.right,
            COMMON_STYLES.top,
            styles.orderStatusLabel,
            bgColor,
          ]}
        >
          <Text
            style={[TYPOGRAPHY.CAPTION1, VARIANTS_STYLES.MEDIUM, textColor]}
          >
            {getUpdatedStatus()}
          </Text>
        </View>
      </View>
      <View
        style={[
          COMMON_STYLES.ph1,
          COMMON_STYLES["pv5/4"],
          COMMON_STYLES.row,
          COMMON_STYLES.justifyStart,
          styles.gap1,
        ]}
      >
        {orderLines
          .slice(0, orderLines.length > 3 ? 2 : orderLines.length)
          .map((line) => (
            <View
              style={[
                styles.img80X90,
                COMMON_STYLES.b1,
                BORDER_STYLES.borderNeutral10,
                COMMON_STYLES["br1/2"],
              ]}
              key={line.id}
            >
              <View
                style={[
                  COMMON_STYLES.flex1,
                  COMMON_STYLES.alignItemsCenter,
                  COMMON_STYLES.justifyCenter,
                ]}
              >
                <LazyImage
                  imageUrl={line.product.image_url}
                  imgStyles={styles.img44X34}
                  fallBackIcon={<ProductIcon height={40} width={40} />}
                />
              </View>

              <View
                style={[
                  BG_STYLES.bgNeutral10,
                  COMMON_STYLES.fullWidth,
                  COMMON_STYLES.alignItemsCenter,
                  COMMON_STYLES.justifyCenter,
                  COMMON_STYLES["ph1/2"],
                  styles.h18,
                ]}
              >
                <Text
                  style={[TYPOGRAPHY.CAPTION2, VARIANTS_STYLES.MEDIUM]}
                  numberOfLines={1}
                >
                  {line.product_name}
                </Text>
              </View>
            </View>
          ))}
        {orderLines.length > 3 && (
          <ImageCountView count={orderLines.length - 2} />
        )}
      </View>
      {/* <Pressable
        onPress={() =>
          rnNavigation({ name: STFRoutes.ORDER_DETAILS, params: { orderId } })
        }
        style={[
          COMMON_STYLES.row,
          COMMON_STYLES.justifyCenter,
          BG_STYLES.bgSuccess10,
          COMMON_STYLES.alignItemsCenter,
          styles.h48,
        ]}
      >
        <Text
          style={[
            TYPOGRAPHY.TEXT2,
            VARIANTS_STYLES.SEMI_EMPHASIZED,
            TEXT_STYLES.textPrimary100,
          ]}
        >
          Order Details
        </Text>
        <CaretDownIcon
          svgProps={{
            ...styles.caretIcon,
            style: styles.rotateIcon,
          }}
          pathProps={{ stroke: COLORS.primary100 }}
        />
      </Pressable> */}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  caretIcon: { height: 24, width: 24 },
  rotateIcon: {
    transform: [{ rotate: "-90deg" }],
  },
  img80X90: {
    height: 80,
    width: 90,
  },
  img44X34: {
    height: 44,
    width: 34,
  },
  container: {
    width: 328,
  },
  header: {
    borderBottomWidth: 1,
    borderBottomColor: COLORS.blue20,
  },
  gap1: {
    gap: 16,
  },
  h48: {
    height: 48,
  },
  h18: {
    height: 18,
  },
  orderStatusLabel: {
    borderBottomLeftRadius: 12,
  },
});

export default OrderListingCard;
